import { Editor } from '@tiptap/react';

import { TIconName } from '@payaca/untitled-icons';

export type EditorToolbarItem =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'underline'
  | 'code'
  | 'codeBlock'
  | 'link'
  | 'unlink'
  | 'bulletList'
  | 'orderedList'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'blockquote'
  | 'undo'
  | 'redo';

export interface EditorToolbarAction {
  label: string;
  icon: TIconName;
  isActive?: (editor: Editor) => boolean;
  onClick: (editor: Editor) => void;
  disabled?: (editor: Editor) => boolean;
}

/**
 * Default toolbar shared by `RichTextarea` and `MarkdownTextarea`. Items beyond
 * this set (headings, blockquote, code, etc.) are available via the `toolbar`
 * prop.
 */
export const DEFAULT_TOOLBAR: EditorToolbarItem[] = [
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'link',
  'unlink',
  'undo',
  'redo',
];

/**
 * Default toolbar for the borderless/inline editor, surfaced as a bubble menu
 * on text selection. Limited to selection-relevant actions — undo/redo and
 * other document-level commands don't belong on a selection bubble.
 */
export const DEFAULT_INLINE_TOOLBAR: EditorToolbarItem[] = [
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'link',
  'unlink',
];

type ChainCommands = ReturnType<Editor['chain']>;

/**
 * Build a toggle-style toolbar action. `exec` runs against either
 * `editor.chain().focus()` (real execution) or `editor.can().chain().focus()`
 * (a probe), both of which expose the same toggle commands.
 */
const toggleAction = (
  label: string,
  icon: TIconName,
  markOrNode: string,
  exec: (chain: ChainCommands) => ChainCommands,
  isActiveOptions?: Record<string, unknown>
): EditorToolbarAction => ({
  label,
  icon,
  isActive: (editor) =>
    isActiveOptions
      ? editor.isActive(markOrNode, isActiveOptions)
      : editor.isActive(markOrNode),
  onClick: (editor) => exec(editor.chain().focus()).run(),
  disabled: (editor) => !exec(editor.can().chain().focus()).run(),
});

export const EDITOR_TOOLBAR_ACTIONS: Record<
  EditorToolbarItem,
  EditorToolbarAction
> = {
  bold: toggleAction('Bold', 'bold-01.3', 'bold', (c) => c.toggleBold()),
  italic: toggleAction('Italic', 'italic-01.3', 'italic', (c) =>
    c.toggleItalic()
  ),
  strike: toggleAction('Strikethrough', 'strikethrough-01.3', 'strike', (c) =>
    c.toggleStrike()
  ),
  underline: toggleAction('Underline', 'underline-01.3', 'underline', (c) =>
    c.toggleUnderline()
  ),
  code: toggleAction('Inline code', 'brackets.3', 'code', (c) =>
    c.toggleCode()
  ),
  codeBlock: toggleAction('Code block', 'brackets.3', 'codeBlock', (c) =>
    c.toggleCodeBlock()
  ),
  link: {
    label: 'Link',
    icon: 'link-01.3',
    isActive: (editor) => editor.isActive('link'),
    onClick: (editor) => {
      const linkHref: unknown = editor.getAttributes('link').href;
      const previousUrl = typeof linkHref === 'string' ? linkHref : '';
      const promptResult = window.prompt('URL', previousUrl);
      if (promptResult === null) return;
      const url = promptResult.trim();
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      // With no selection and not in a link, `extendMarkRange` is a no-op
      // and `setLink` would silently fail. Insert the URL as linked text
      // instead so the toolbar action always has a visible effect.
      if (editor.state.selection.empty && !editor.isActive('link')) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: url,
            marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }],
          })
          .run();
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: '_blank' })
        .run();
    },
  },
  unlink: {
    label: 'Unlink',
    icon: 'link-broken-01.3',
    onClick: (editor) => editor.chain().focus().unsetLink().run(),
    disabled: (editor) => !editor.isActive('link'),
  },
  bulletList: toggleAction('Bullet list', 'dotpoints-01.3', 'bulletList', (c) =>
    c.toggleBulletList()
  ),
  orderedList: toggleAction(
    'Ordered list',
    'list-ordered',
    'orderedList',
    (c) => c.toggleOrderedList()
  ),
  heading1: toggleAction(
    'Heading 1',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 1 }),
    { level: 1 }
  ),
  heading2: toggleAction(
    'Heading 2',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 2 }),
    { level: 2 }
  ),
  heading3: toggleAction(
    'Heading 3',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 3 }),
    { level: 3 }
  ),
  blockquote: toggleAction(
    'Blockquote',
    'message-text-square-01.3',
    'blockquote',
    (c) => c.toggleBlockquote()
  ),
  undo: {
    label: 'Undo',
    icon: 'flip-backward.3',
    onClick: (editor) => editor.chain().focus().undo().run(),
    disabled: (editor) => !editor.can().chain().focus().undo().run(),
  },
  redo: {
    label: 'Redo',
    icon: 'flip-forward.3',
    onClick: (editor) => editor.chain().focus().redo().run(),
    disabled: (editor) => !editor.can().chain().focus().redo().run(),
  },
};
